import { TID } from '@/lib/testIds';
import { ArrowUpRight, MapPin, Sparkles } from 'lucide-react';

const HERO_PORTRAIT =
  'https://customer-assets.emergentagent.com/job_sejal-creative/artifacts/cm733pnd_WhatsApp%20Image%202026-07-11%20at%208.40.01%20PM.jpeg?v=2';

const chips = [
  { n: '01', label: 'Business Strategy' },
  { n: '02', label: 'Operations Excellence' },
  { n: '03', label: 'Organization Development' },
  { n: '04', label: 'Digital Transformation' },
];

export default function Hero() {
  return (
    <section
      id="top"
      data-testid={TID.hero.root}
      className="relative min-h-screen pt-28 md:pt-32 pb-16 md:pb-20 overflow-hidden"
    >
      {/* Ember glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 -right-40 w-[900px] h-[900px] ember-glow opacity-95 -z-0"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-52 -left-40 w-[700px] h-[700px] ember-glow opacity-70 -z-0"
      />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 md:gap-10 items-center">
          {/* Left: Big display */}
          <div className="col-span-12 lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 px-3 py-1.5 text-[11px] uppercase tracking-[0.24em] text-text/80 bg-white/[0.03]">
              <Sparkles size={12} className="text-ember" /> Hey, I am
            </div>

            <h1
              data-testid={TID.hero.name}
              className="mt-6 font-display font-extrabold tracking-tight text-text leading-[0.9]"
              style={{ fontSize: 'clamp(48px, 7vw, 108px)' }}
            >
              BUSINESS
              <br />
              <span className="text-ember">CONSULTANT.</span>
            </h1>

            <p
              data-testid={TID.hero.title}
              className="mt-6 text-[11px] uppercase tracking-[0.3em] text-text/60 font-medium"
            >
              Sejal Patil — Business Strategy &amp; Transformation
            </p>

            <p
              data-testid={TID.hero.tagline}
              className="mt-6 max-w-xl text-[15px] md:text-base leading-relaxed text-text/75"
            >
              I help founders, developers, family businesses, and growing
              organizations transform complexity into scalable systems —
              through strategy, operations, organizational design, and
              disciplined execution.
            </p>

            {/* Search-like CTA row */}
            <div className="mt-9 flex flex-col sm:flex-row items-stretch gap-3 max-w-lg">
              <div className="flex-1 flex items-center gap-3 ghost-input">
                <MapPin size={14} className="text-text-70 shrink-0" />
                <input
                  type="text"
                  aria-label="Your business focus"
                  placeholder="What are you looking to solve?"
                  className="bg-transparent outline-none w-full text-sm placeholder:text-text-50"
                />
              </div>
              <a
                href="#contact"
                data-testid={TID.hero.ctaBook}
                className="btn-ember rounded-full px-6 py-3.5 text-[12px] uppercase tracking-[0.18em] font-semibold inline-flex items-center justify-center gap-2 shrink-0"
              >
                Schedule a Free Call
                <ArrowUpRight size={14} />
              </a>
            </div>

            <a
              href="#services"
              data-testid={TID.hero.ctaView}
              className="mt-6 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.2em] text-text/70 hover:text-ember transition-colors"
            >
              View Services
              <ArrowUpRight size={12} />
            </a>
          </div>

          {/* Right: Portrait with glow */}
          <div className="col-span-12 lg:col-span-5 relative">
            <div className="relative mx-auto max-w-md">
              <div
                aria-hidden
                className="absolute inset-0 -m-10 ember-glow -z-10"
                style={{ borderRadius: '50%' }}
              />
              <div
                data-testid={TID.hero.portrait}
                className="relative overflow-hidden rounded-[36px] border border-white/10 bg-ink-2"
                style={{ aspectRatio: '4/5' }}
              >
                <img
                  src={HERO_PORTRAIT}
                  alt="Portrait of Sejal Patil"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: 'center 60%', transform: 'scale(1.35)', transformOrigin: 'center 60%' }}
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent pointer-events-none" />
                <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between text-text pointer-events-none">
                  <div>
                    <div className="mt-1 font-display font-semibold text-lg">
                      Mumbai, India
                    </div>
                  </div>
                  <div className="h-9 w-9 rounded-full grid place-items-center bg-ember text-ink">
                    <ArrowUpRight size={16} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Numbered service chips */}
        <div className="mt-14 md:mt-20 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {chips.map((c) => (
            <div
              key={c.n}
              className="flex items-center gap-3 rounded-full border border-white/12 bg-white/[0.03] px-4 py-3 text-sm text-text/85 hover:border-ember/60 hover:bg-white/[0.06] transition-colors"
            >
              <span className="font-display text-ember font-semibold">{c.n}</span>
              <span className="text-[13px]">{c.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
